import { type InferType, isValidPhoneNumber, type Schema, yup } from '@faceup/validation'
import moment from 'moment-timezone'
import type { CaseImportAllowedFormItemType } from './constants'
import {
  CaseJustification,
  CasePriority,
  CaseSourceType,
  DEFAULT_REGION,
  FormItemType,
  SupportedRegion,
  UserType,
} from './enums'
import { convertRegionToSupportedRegion } from './localization/converters'
import { LONG_STRING_MAX_LENGTH, SHORT_STRING_MAX_LENGTH } from './validationConfig'

// Backward-compatible re-export of validation primitives that moved to @faceup/validation
export {
  type CountryCode,
  getCountryCallingCode,
  getPasswordStrengthScore,
  isBase64,
  isBase64UrlSafeNoPadding,
  isCompanyEmail,
  isDomain,
  isEmail,
  isHexColor,
  isIP,
  isLength,
  isNumeric,
  isPasswordValid,
  isStrongPassword,
  isUrl,
  isUUIDv7,
  isValidPhoneNumber,
  isWholeNumber,
} from '@faceup/validation'

type PrevCurrValue = string | boolean | number | number[] | string[] | null | undefined

const editFormItemAnswerSchema = yup.object().shape({
  formItemId: yup.string().required(),
  value: yup.string().required().nullable(),
})

type EditFormItemAnswer = InferType<typeof editFormItemAnswerSchema>

export const isEditFormItemAnswerType = (value: unknown): value is EditFormItemAnswer => {
  return editFormItemAnswerSchema.isValidSync(value)
}

const editReportSchema = yup.object().shape({
  body: yup.string().required().nullable(),
  body_nonce: yup.string().required().nullable(),
  created_at: yup.string().required(),
  category_id: yup.number().required(),
  // these are not required for older audit logs
  organizational_unit_id: yup.string().nullable(),
  source: yup.string<CaseSourceType>().oneOf(Object.values(CaseSourceType)).nullable(),
  category_name: yup.string().nullable(),
  organizational_unit_name: yup.string().nullable(),
})

const editReportReturnTypeSchema = yup.object().shape({
  body: yup.string().required().nullable(),
  body_nonce: yup.string().required().nullable(),
  created_at: yup.string().required(),
  category_id: yup.string().required(),
  // these are not required for older audit logs
  organizational_unit_id: yup.string().nullable(),
  source: yup.string<CaseSourceType>().oneOf(Object.values(CaseSourceType)).nullable(),
  category_name: yup.string().nullable(),
  organizational_unit_name: yup.string().nullable(),
})

type EditReport = InferType<typeof editReportSchema>
type EditReportReturnType = InferType<typeof editReportReturnTypeSchema>

export const isEditReportType = (value: unknown): value is EditReport => {
  return editReportSchema.isValidSync(value)
}

export const isEditReportReturnType = (value: unknown): value is EditReportReturnType => {
  return editReportReturnTypeSchema.isValidSync(value)
}

const changedCategoryReturnSchema = yup.object().shape({
  category_id: yup.number().required(),
  category_name: yup.string(),
})

type ChangedCategoryReturn = InferType<typeof changedCategoryReturnSchema>

export const isChangedCategoryReturnType = (value: unknown): value is ChangedCategoryReturn => {
  return changedCategoryReturnSchema.isValidSync(value)
}

const internalCommentReturnSchema = yup.object().shape({
  ciphertext: yup.string().required().nullable(),
  nonce: yup.string().required(),
  attachments: yup
    .array(
      yup.object().shape({
        id: yup.string().required(),
        name: yup.string().required(),
      })
    )
    .required(),
})

const internalCommentSchema = yup.object().shape({
  ciphertext: yup.string().required().nullable(),
  nonce: yup.string().required(),
  attachments: yup
    .array(
      yup.object().shape({
        id: yup.number().required(),
        name: yup.string().required(),
      })
    )
    .required(),
})

type ChangedInternalComment = InferType<typeof internalCommentSchema>
export type ChangedInternalCommentReturn = InferType<typeof internalCommentReturnSchema>

export const isChangedInternalCommentType = (
  value: unknown
): value is ChangedInternalCommentReturn => {
  return internalCommentReturnSchema.isValidSync(value)
}

const deletedInternalCommentReturnSchema = internalCommentReturnSchema

type DeletedInternalComment = InferType<typeof deletedInternalCommentReturnSchema>

export const isDeletedInternalReturnCommentType = (
  value: unknown
): value is DeletedInternalComment => {
  return deletedInternalCommentReturnSchema.isValidSync(value)
}

const casePrioritySchema = yup.mixed<CasePriority>().oneOf(Object.values(CasePriority)).required()

export const isCasePriorityType = (value: unknown): value is CasePriority => {
  return casePrioritySchema.isValidSync(value)
}

const reportJustificationSchema = yup
  .mixed<CaseJustification>()
  .oneOf(Object.values(CaseJustification))
  .required()

export const isReportJustificationType = (value: unknown): value is CaseJustification => {
  return reportJustificationSchema.isValidSync(value)
}

const auditLogJustificationReturnSchema = yup.object().shape({
  closingReasonId: yup.string().required().nullable(),
  closingCommentBody: yup.string().required().nullable(),
  closingCommentNonce: yup.string().required().nullable(),
})

export type AuditLogJustificationReturn = InferType<typeof auditLogJustificationReturnSchema>

export const isAuditLogJustificationReturnType = (
  value: unknown
): value is AuditLogJustificationReturn => {
  return auditLogJustificationReturnSchema.isValidSync(value)
}

const investigationTagSchema = yup.object().shape({
  investigationTag: yup.string().required(),
})

export type InvestigationTag = InferType<typeof investigationTagSchema>

export const isInvestigationTagType = (value: unknown): value is InvestigationTag => {
  return investigationTagSchema.isValidSync(value)
}

export type PrevCurrPossibleValues =
  | EditFormItemAnswer
  | EditReport
  | PrevCurrValue
  | ChangedInternalComment
  | DeletedInternalComment
  | AuditLogJustificationReturn
  | InvestigationTag

export type PrevCurrPossibleValuesReturn =
  | EditFormItemAnswer
  | EditReportReturnType
  | PrevCurrValue
  | ChangedInternalCommentReturn
  | DeletedInternalComment
  | ChangedCategoryReturn
  | CasePriority
  | CaseJustification
  | AuditLogJustificationReturn

export type CaseImportErrorMessages = {
  formItemNotInChannel: string
  formItemInChannelButNotInCategory: string
  formItemIsRequired: string
  invalidOrganizationalUnitId: string
  invalidStatusId: string
  invalidClosingReasonId: string
  invalidCategoryId: string
  invalidChannelId: string
  invalidLabel: string
  invalidOption: string
  invalidPhoneNumber: string
  invalidEmail: string
  invalidDate: string
  invalidNumber: string
  organizationalUnitNotInChannel: string
  categoryNotInChannel: string
}

// for BE, we don't need translated error messages
const defaultCaseImportErrorMessages: CaseImportErrorMessages = {
  formItemNotInChannel: 'Form item is not present in this channel',
  formItemInChannelButNotInCategory:
    'Form item is present in this channel but not in this category',
  formItemIsRequired: 'This field is required',
  invalidOrganizationalUnitId:
    'Invalid organizationalUnitId - not found among your organizational units',
  invalidStatusId: 'Invalid statusId - not found among your statuses',
  invalidClosingReasonId: 'Invalid closingReasonId - not found among your closing reasons',
  invalidCategoryId: 'Invalid categoryId - not found among your categories',
  invalidChannelId: 'Invalid channelId - not found among your channels',
  invalidLabel: 'Invalid label - not found among your labels',
  invalidOption: 'Invalid option',
  invalidPhoneNumber: 'Invalid phone number',
  invalidEmail: 'Invalid email address',
  invalidDate: 'Invalid date format',
  invalidNumber: 'Invalid number',
  organizationalUnitNotInChannel: 'Organizational unit is not assigned to this channel',
  categoryNotInChannel: 'Category is not assigned to this channel',
}

export type ReportSchemaParams = {
  statusesIds: string[]
  closingReasonIds: string[]
  channels: {
    id: number
    formItemsInForm: {
      formItemId: string
      categoryId: number
      isRequired: boolean
    }[]
    organizationalUnitIds: string[]
    categoryIds: number[]
  }[]
  organizationalUnitIds: string[]
  categoriesIds: number[]
  labelIds: string[]
  formItems: {
    id: string
    type: CaseImportAllowedFormItemType
    optionIds: number[]
  }[]
  errorMessages: CaseImportErrorMessages
}

const fromGlobalId = (id: string) => Number(atob(id).split(':')[1])

export class ReportImportSchemaBuilder {
  private readonly statusesIds: string[]
  private readonly closingReasonIds: string[]
  private readonly channels: ReportSchemaParams['channels']
  private readonly organizationalUnitIds: string[]
  private readonly categoriesIds: number[]
  private readonly labelIds: string[]
  private readonly formItems: ReportSchemaParams['formItems']
  private readonly errorMessages: CaseImportErrorMessages

  constructor(params: ReportSchemaParams) {
    this.statusesIds = params.statusesIds
    this.closingReasonIds = params.closingReasonIds
    this.channels = params.channels
    this.organizationalUnitIds = params.organizationalUnitIds
    this.categoriesIds = params.categoriesIds
    this.labelIds = params.labelIds
    this.formItems = params.formItems
    this.errorMessages = params.errorMessages
  }

  private addFormItemValidation(schema: Schema, formItemId: string) {
    const errorMessages = this.errorMessages
    const channels = this.channels

    return schema.test('formItemInChannel', errorMessages.formItemNotInChannel, function (value) {
      const channel = channels.find(channel => channel.id === this.parent.channelId)
      if (!channel) {
        return false // should never happen
      }

      const isInChannel = channel.formItemsInForm.some(
        formItem => formItem.formItemId === formItemId
      )

      const channelCategoryConfig = channel.formItemsInForm.find(
        formItem =>
          formItem.formItemId === formItemId && formItem.categoryId === this.parent.categoryId
      )

      const isInChannelAndCategory = channelCategoryConfig !== undefined

      const isRequired = channelCategoryConfig ? channelCategoryConfig.isRequired : false

      if (!value && !isRequired) {
        return true
      }

      if (!isInChannel) {
        return this.createError({
          message: errorMessages.formItemNotInChannel,
          params: { formItemId, channelId: this.parent.channelId },
        })
      }

      if (!isInChannelAndCategory) {
        return this.createError({
          message: errorMessages.formItemInChannelButNotInCategory,
          params: {
            formItemId,
            channelId: this.parent.channelId,
            categoryId: this.parent.categoryId,
          },
        })
      }

      if (!value && value !== 0 && isRequired) {
        return this.createError({
          message: errorMessages.formItemIsRequired,
          params: {
            formItemId,
            channelId: this.parent.channelId,
            categoryId: this.parent.categoryId,
          },
        })
      }

      return true
    })
  }

  private getBaseReportFields() {
    const channels = this.channels

    return {
      submittedAt: yup.date().required(),
      organizationalUnitId: yup
        .string()
        .required()
        .test({
          name: 'organizationalUnitId',
          message: this.errorMessages.invalidOrganizationalUnitId,
          test: value => this.organizationalUnitIds.includes(value),
        })
        .test(
          'organizationalUnitIdInChannel',
          this.errorMessages.organizationalUnitNotInChannel,
          function (value) {
            const rawChannelId = this.parent?.channelId
            const channelId =
              typeof rawChannelId === 'string' ? fromGlobalId(rawChannelId) : Number(rawChannelId)

            const channel = channels.find(channel => channel.id === channelId)
            if (!channel) {
              return true // defer to channelId validation
            }

            return channel.organizationalUnitIds.includes(value)
          }
        ),
      statusId: yup
        .string()
        .required()
        .test({
          name: 'statusId',
          message: this.errorMessages.invalidStatusId,
          test: value => this.statusesIds.includes(value),
        }),
      source: yup.string().oneOf(Object.values(CaseSourceType)).required(),
      categoryId: yup
        .number()
        .required()
        .transform((_, value) => (typeof value === 'string' ? fromGlobalId(value) : Number(value)))
        .test({
          name: 'categoryId',
          message: this.errorMessages.invalidCategoryId,
          test: value => this.categoriesIds.includes(value),
        })
        .test('categoryIdInChannel', this.errorMessages.categoryNotInChannel, function (value) {
          const rawChannelId = this.parent?.channelId
          const channelId =
            typeof rawChannelId === 'string' ? fromGlobalId(rawChannelId) : Number(rawChannelId)

          const channel = channels.find(channel => channel.id === channelId)
          if (!channel) {
            return true // defer to channelId validation
          }

          return channel.categoryIds.includes(value)
        }),
      priority: yup.string().oneOf(Object.values(CasePriority)).nullable(),
      justification: yup.string().oneOf(Object.values(CaseJustification)).nullable(),
      channelId: yup
        .number()
        .required()
        .transform((_, value) => (typeof value === 'string' ? fromGlobalId(value) : Number(value)))
        .test({
          name: 'channelId',
          message: this.errorMessages.invalidChannelId,
          test: value => this.channels.map(c => c.id).includes(value),
        }),
      closingReasonId: yup
        .string()
        .nullable()
        .test({
          name: 'closingReasonId',
          message: this.errorMessages.invalidClosingReasonId,
          test: value => !value || this.closingReasonIds.includes(value),
        }),
      dueDate: yup.date().nullable(),
      isHiddenFromStatistics: yup.boolean().nullable(),
      // a comma separated string or an array where the values are the label ids
      labelIds: yup
        .mixed<string[]>()
        .nullable()
        .transform(value => {
          if (typeof value === 'string') {
            return value
              .split(';')
              .map(item => item.trim())
              .filter(item => item.length > 0)
          }

          if (Array.isArray(value)) {
            return value
          }

          return value
        })
        .test('labelId', this.errorMessages.invalidLabel, value => {
          if (value == null) {
            return true
          }
          if (!Array.isArray(value)) {
            return false
          }
          return value.every(item => this.labelIds.includes(item))
        }),
    }
  }

  private createCustomFormItemSchema({
    id,
    type,
    optionIds,
  }: {
    id: string
    type: CaseImportAllowedFormItemType
    optionIds: number[]
  }): Schema {
    const selectSchema = yup
      .string()
      .test('ValidOption', this.errorMessages.invalidOption, value => {
        if (!value) {
          return true
        }
        return optionIds.includes(fromGlobalId(value))
      })
      .nullable()

    const schemas: Record<CaseImportAllowedFormItemType, Schema> = {
      [FormItemType.Number]: yup
        .number()
        .typeError(this.errorMessages.invalidNumber)
        .integer(this.errorMessages.invalidNumber)
        .moreThan(-1, this.errorMessages.invalidNumber)
        .nullable(),
      [FormItemType.Select]: selectSchema,
      [FormItemType.Scale]: selectSchema,
      [FormItemType.MultiSelect]: yup
        .mixed<string[]>()
        .nullable()
        .transform(value => {
          if (typeof value === 'string') {
            return value
              .split(';')
              .map(item => item.trim())
              .filter(item => item.length > 0)
          }
          return value
        })
        .test('ValidOption', this.errorMessages.invalidOption, value => {
          if (!value) {
            return true
          }
          if (!Array.isArray(value)) {
            return false
          }
          return value.every(item => optionIds.includes(fromGlobalId(item)))
        }),
      [FormItemType.Email]: yup.string().email(this.errorMessages.invalidEmail).trim().nullable(),
      [FormItemType.Date]: yup
        .string()
        .transform((value, originalValue) => {
          // string() casts Date objects with toString() by default, but we want to cast to ISO string instead
          if (originalValue instanceof Date) {
            return originalValue.toISOString()
          }

          return value
        })
        .transform(value => {
          if (!value) {
            return null
          }

          const parsed = moment(value, moment.ISO_8601, true)
          if (parsed.isValid()) {
            return parsed.toISOString()
          } else {
            return value
          }
        })
        .test('ValidDate', this.errorMessages.invalidDate, value => {
          if (!value) {
            return true
          }
          return moment(value, moment.ISO_8601, true).isValid()
        })
        .nullable(),
      [FormItemType.SimpleText]: yup.string().max(SHORT_STRING_MAX_LENGTH).nullable(),
      [FormItemType.MultilineText]: yup.string().max(LONG_STRING_MAX_LENGTH).nullable(),
      [FormItemType.PhoneNumber]: yup
        .string()
        .test('is-valid-phone-number', this.errorMessages.invalidPhoneNumber, value => {
          if (!value) {
            return true
          }
          return isValidPhoneNumber(value)
        })
        .nullable(),
    }

    return this.addFormItemValidation(schemas[type], id)
  }

  private getCustomFormItemFields() {
    return this.formItems.reduce(
      (acc, formItem) => {
        acc[formItem.id] = this.createCustomFormItemSchema(formItem)
        return acc
      },
      {} as Record<string, Schema>
    )
  }

  private createReportImportFields() {
    const baseFields = this.getBaseReportFields()
    const customFormItemFields = this.getCustomFormItemFields()

    return {
      ...baseFields,
      ...customFormItemFields,
    }
  }

  public createUnencryptedSchema() {
    return yup
      .array()
      .of(
        yup
          .object()
          .transform(value => {
            if (!value || typeof value !== 'object') {
              return value
            }

            const transformedValue = { ...value }

            // Columns can be in format: "<field-name> (id: <field-id>)"
            // convert those to just "<field-id>"
            Object.keys(value).forEach(key => {
              const match = key.match(/^(.+) \(id: (.+)\)$/)
              if (match?.[2]) {
                const fieldId = match[2]
                transformedValue[fieldId] = value[key]
                delete transformedValue[key]
              }
            })

            return transformedValue
          })
          .shape({
            ...this.createReportImportFields(),
            moreInfo: yup.string().max(LONG_STRING_MAX_LENGTH).required(),
            closingComment: yup.string().nullable(),
            senderName: yup.string().nullable(),
          })
          .noUnknown()
      )
      .required()
      .min(1)
  }

  public createEncryptedSchema() {
    return yup
      .array()
      .of(
        yup.object(this.createReportImportFields()).concat(
          yup.object({
            body: yup.string().required(),
            bodyNonce: yup.string().required(),
            closingComment: yup
              .object({
                body: yup.string().required(),
                nonce: yup.string().required(),
              })
              .nullable(),
            recipientKeys: yup
              .array()
              .of(
                yup.object({
                  id: yup.string().nullable(),
                  key: yup.string().required(),
                })
              )
              .required()
              .min(1),
          })
        )
      )
      .required()
      .min(1)
  }
}

export const createReportImportUnencryptedSchema = (params: ReportSchemaParams) => {
  const builder = new ReportImportSchemaBuilder(params)
  return builder.createUnencryptedSchema()
}

export const createReportImportEncryptedSchema = (
  params: Omit<ReportSchemaParams, 'errorMessages'>
) => {
  const paramsWithErrorMessages: ReportSchemaParams = {
    ...params,
    errorMessages: defaultCaseImportErrorMessages,
  }

  const builder = new ReportImportSchemaBuilder(paramsWithErrorMessages)
  return builder.createEncryptedSchema()
}

export const jwtPayloadSchema = yup.object().shape({
  id: yup.string().required(),
  userType: yup.string<UserType>().oneOf(Object.values(UserType)).required(),
  tokenVersion: yup.number().required(),
  region: yup
    .array()
    .of(yup.string<SupportedRegion>().oneOf(Object.values(SupportedRegion)).required())
    .required(),
})

export type JwtPayload = InferType<typeof jwtPayloadSchema>

export const isDefaultRegion = (region: SupportedRegion) => {
  return region === convertRegionToSupportedRegion(DEFAULT_REGION)
}
